import React from "react";
import { toast } from "sonner";

import { ClientFactory, type DiscussionMuteSurface } from "@/main/common/ClientFactory";
import type { Localizer } from "@/main/react/Types";
import logger from "@/lib/logger";

export type DiscussionNotificationMuteController = {
    /** The viewer muted this post. Meaningful once `known`. */
    muted: boolean;
    /** The server has answered once (the state read, or a mute/unmute response). */
    known: boolean;
    /** A mute or unmute is in flight. */
    pending: boolean;
    /** Mute when not muted, unmute when muted; resolves once the server has answered. */
    toggle: (surface: DiscussionMuteSurface) => Promise<void>;
};

/**
 * "Mute this post" (FRR 2b, FI-612): the one state behind the post menu's "Mute this post" / "Unmute this post"
 * item. Design: `designs/01-now/2026-08-01-push-correctness-hardening-and-scope-expansion-design.md` §7.
 *
 * - **No DTO carries the state**, and the post page must not pay a request per view for a menu item most members
 *   never open, so the first read is a GET made only once `enabled` turns true. `DiscussionButtons` passes the
 *   `…` menu's own open state, so the read happens the first time the member opens the menu on the full post page.
 * - **Written only from a server answer.** A mute is idempotent on the server (a repeat answers `muted: true` and
 *   records nothing), so a tap made before the first read resolves is still safe.
 * - The toast is the settled copy (§2): "You won't get notifications about this post". Unmuting needs none: the
 *   item's label is the confirmation.
 */
export function useDiscussionNotificationMute(
    discussionPublicId: string,
    localizer: Localizer,
    enabled: boolean,
): DiscussionNotificationMuteController {
    const [muted, setMuted] = React.useState(false);
    const [known, setKnown] = React.useState(false);
    const [pending, setPending] = React.useState(false);
    const mutedRef = React.useRef(false);
    const pendingRef = React.useRef(false);
    const requestedRef = React.useRef<string | null>(null);

    const confirm = React.useCallback((next: boolean) => {
        mutedRef.current = next;
        setMuted(next);
        setKnown(true);
    }, []);

    // A different post re-arms the read.
    React.useEffect(() => {
        requestedRef.current = null;
        mutedRef.current = false;
        setMuted(false);
        setKnown(false);
    }, [discussionPublicId]);

    React.useEffect(() => {
        if (!enabled || !discussionPublicId || requestedRef.current === discussionPublicId) {
            return undefined;
        }
        requestedRef.current = discussionPublicId;
        let active = true;
        ClientFactory.getDiscussionNotificationMute(discussionPublicId)
            .then(({ data }) => {
                if (active) {
                    confirm(Boolean(data?.muted));
                }
            })
            .catch((error: unknown) => {
                logger.warn("[useDiscussionNotificationMute] state read failed", error);
            });
        return () => {
            active = false;
        };
    }, [enabled, discussionPublicId, confirm]);

    const toggle = React.useCallback(async (surface: DiscussionMuteSurface) => {
        if (!discussionPublicId || pendingRef.current) {
            return;
        }
        pendingRef.current = true;
        setPending(true);
        const muting = !mutedRef.current;
        try {
            const { data } = muting
                ? await ClientFactory.muteDiscussionNotifications(discussionPublicId, surface)
                : await ClientFactory.unmuteDiscussionNotifications(discussionPublicId, surface);
            confirm(Boolean(data?.muted));
            if (muting) {
                toast.success(localizer("notifications.mute_post.toast"));
            }
        } catch (error) {
            logger.error("[useDiscussionNotificationMute] toggle failed", error);
            toast.error(localizer("shared.action.failed"));
        } finally {
            pendingRef.current = false;
            setPending(false);
        }
    }, [discussionPublicId, localizer, confirm]);

    return { muted, known, pending, toggle };
}
